import * as React from 'react';
import { LiveClassCohort } from '@magoosh/admin/live_class_sessions/types';
import { DefaultButton, PrimaryButton, TabButton } from '@magoosh/lib/button';
import { adminPaths } from 'config/path_helpers';
import { DataTable } from '@magoosh/lib/data_table';
import useDataQuery from '@magoosh/lib/data_table/use_data_query';
import { useMemo, useState } from 'react';
import Modal from '@magoosh/lib/modal';
import { PageProps } from '@magoosh/admin/live_class_cohorts/index/types';
import CohortForm from '@magoosh/admin/live_class_cohorts/index/form';
import BulkCohortRegistration from '@magoosh/admin/live_class_cohorts/index/bulk_registration';
import DropdownList from 'react-widgets/lib/DropdownList';
import { Col, Row } from 'react-bootstrap';
import { Search } from '@magoosh/lib/data_table/search';
import { QueryClient, QueryClientProvider } from 'react-query';

const LiveClassCohortsIndex: React.FC<PageProps> = (props) => {
  const defaultQueryParams = useMemo(() => {
    const thoseParams = { filters: {} };
    return thoseParams;
  }, []);

  const { runQuery, dataQueryTableProps } = useDataQuery(
    adminPaths.api.liveClassCohorts,
    defaultQueryParams
  );

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCohort, setEditingCurrentCohort] = useState(null);
  const [registeringCohort, setRegisteringCohort] = useState(null);

  const onClickNew = () => {
    setEditingCurrentCohort(null);
    setShowFormModal(true);
  };

  const onClickEdit = (cohort: LiveClassCohort) => {
    setEditingCurrentCohort(cohort);
    setShowFormModal(true);
  };

  return (
    <div>
      <div>
        <div className="text-center">
          <TabButton href={adminPaths.liveClassSessions()}>Sessions</TabButton>
          <TabButton disabled>Cohorts</TabButton>
          <TabButton href={adminPaths.liveClasses()}>Classes</TabButton>
        </div>
        <div>
          <h1>Cohorts</h1>
          <p>
            These are used to determine which sessions are shown to a user. A cohort can either belong to a
            Branding or a Subscription Plan. If it belongs to a Branding, all students in that Branding may
            see the associated class sessions listed. If it belongs to a Subscription Plan, only students
            subscribed to that Plan will see the associated class sessions.
          </p>
        </div>
      </div>
      <DataTable
        {...dataQueryTableProps}
        columns={[
          {
            name: 'Name',
            selector: 'name'
          },
          {
            name: 'Type',
            selector: 'attachableType'
          },
          {
            name: 'Attached To',
            selector: 'attachableName'
          }
        ]}>
        <DataTable.HeaderActions>
          <PrimaryButton icon="plus" onClick={onClickNew}>
            New
          </PrimaryButton>
        </DataTable.HeaderActions>
        <DataTable.Filters>
          <div className="filters" style={{ width: '800px' }}>
            <Row>
              <Col md={4}>
                <Search runQuery={runQuery} placeholder={'Search Name...'} />
              </Col>
              <Col md={4}>
                <DropdownList
                  data={[
                    { name: 'Active', value: 'true' },
                    { name: 'Inactive', value: 'false' },
                    { name: 'Any', value: 'any' }
                  ]}
                  onChange={(selection) => {
                    runQuery({ active: selection.value });
                  }}
                  placeholder="Filter by active"
                  textField="name"
                  valueField="value"
                  dataKey="value"
                />
              </Col>
              <Col md={4}>
                <DropdownList
                  data={[
                    { name: 'Branding', value: 'Branding' },
                    { name: 'Subscription Plan', value: 'SubscriptionPlan' },
                    { name: 'All', value: 'any' }
                  ]}
                  onChange={(selection) => {
                    runQuery({ attachableType: selection.value });
                  }}
                  placeholder="Cohort type"
                  textField="name"
                  valueField="value"
                  dataKey="value"
                />
              </Col>
            </Row>
          </div>
        </DataTable.Filters>
        <DataTable.ItemActions>
          {(row: LiveClassCohort) => {
            return (
              <>
                <DefaultButton onClick={() => onClickEdit(row)} icon="edit">
                  Edit
                </DefaultButton>
                {row.attachableType === 'SubscriptionPlan' && (
                  <DefaultButton onClick={() => setRegisteringCohort(row)} icon="calendar-check-o">
                    Register
                  </DefaultButton>
                )}
              </>
            );
          }}
        </DataTable.ItemActions>
      </DataTable>
      <Modal
        onHide={() => setShowFormModal(false)}
        show={showFormModal}
        title={editingCohort ? `Edit ${editingCohort.name}` : 'New Cohort'}
        showCloseButton={true}>
        <div>
          <CohortForm
            {...props}
            cohort={editingCohort}
            runQuery={runQuery}
            closeModal={() => setShowFormModal(false)}
          />
        </div>
      </Modal>
      <Modal
        onHide={() => setRegisteringCohort(null)}
        show={registeringCohort !== null}
        title={registeringCohort ? `Bulk registry for ${registeringCohort.name}` : ''}>
        <BulkCohortRegistration {...registeringCohort} />
      </Modal>
    </div>
  );
};

export default (props: PageProps) => {
  const queryClient = new QueryClient();

  return <QueryClientProvider client={queryClient}>
    <LiveClassCohortsIndex {...props} />
  </QueryClientProvider>;
};
